import { useState, useEffect, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import {
  Clock,
  Loader2,
  RefreshCw,
  Copy,
  Eye,
  Trash2,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  FileText,
  Mail,
  MessageSquare,
  BookOpen,
  Target,
  Network,
  CalendarDays,
  Swords,
  TrendingUp,
  Megaphone,
  Share2,
  Users,
  Wand2,
  Globe,
  GitBranch,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useServerPagination } from "@/hooks/useServerPagination";
import { useSortableData } from "@/hooks/useSortableData";
import {
  marketingApi,
  type AIContentItem,
  type AIContentType,
  type AIContentStatus,
} from "@/services/api/marketing";
import { useAuth } from "@/contexts/AuthContext";

export const Route = createFileRoute("/portal/ai-content/history")({
  component: AIContentHistoryPage,
});

const CONTENT_TYPE_LABELS: Record<
  AIContentType,
  { label: string; icon: React.ComponentType<{ className?: string }> }
> = {
  blog_post: { label: "Blog Post", icon: FileText },
  case_study: { label: "Case Study", icon: BookOpen },
  email_sequence: { label: "Email Sequence", icon: Mail },
  social_post: { label: "Social Post", icon: MessageSquare },
  content_brief: { label: "Content Brief", icon: Target },
  topic_cluster: { label: "Topic Cluster", icon: Network },
  content_calendar: { label: "Content Calendar", icon: CalendarDays },
  competitive_analysis: { label: "Competitive Analysis", icon: Swords },
  market_research: { label: "Market Research", icon: TrendingUp },
  campaign_plan: { label: "Campaign Plan", icon: Megaphone },
  social_calendar: { label: "Social Calendar", icon: Share2 },
  audience_insight: { label: "Audience Insight", icon: Users },
};

const STATUS_VARIANTS: Record<
  AIContentStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "secondary",
  generating: "default",
  completed: "outline",
  failed: "destructive",
};

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

function AIContentHistoryPage() {
  const { token } = useAuth();

  // Filters
  const [typeFilter, setTypeFilter] = useState<AIContentType | "all">("all");
  const [statusFilter, setStatusFilter] = useState<AIContentStatus | "all">(
    "all",
  );

  // Server pagination
  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    offset,
    searchTerm,
    setSearchTerm,
    getPaginationInfo,
  } = useServerPagination({ initialItemsPerPage: 25 });

  // Data
  const [items, setItems] = useState<AIContentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Client-side sorting on loaded page
  const { sortedData, requestSort, getSortDirection } = useSortableData(items, {
    initialSort: { key: "created_at", direction: "desc" },
  });

  // Dialogs
  const [viewItem, setViewItem] = useState<AIContentItem | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [improveItem, setImproveItem] = useState<AIContentItem | null>(null);
  const [improveInstructions, setImproveInstructions] = useState("");
  const [isImproving, setIsImproving] = useState(false);
  const [versionsItem, setVersionsItem] = useState<AIContentItem | null>(null);
  const [versions, setVersions] = useState<AIContentItem[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const result = await marketingApi.listAIContent(token, {
        content_type: typeFilter !== "all" ? typeFilter : undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        limit: itemsPerPage,
        offset,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch {
      toast.error("Failed to load content history");
    } finally {
      setIsLoading(false);
    }
  }, [token, typeFilter, statusFilter, itemsPerPage, offset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, statusFilter, setCurrentPage]);

  const { totalPages, totalItems, startIndex, endIndex } =
    getPaginationInfo(total);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const handleDelete = async () => {
    if (!token || !deleteId) return;
    try {
      await marketingApi.deleteAIContent(token, deleteId);
      toast.success("Content deleted");
      setDeleteId(null);
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
    toast.success("Copied to clipboard");
  };

  const handleImprove = async () => {
    if (!token || !improveItem || !improveInstructions.trim()) return;
    setIsImproving(true);
    try {
      await marketingApi.improveAIContent(token, improveItem.id, {
        instructions: improveInstructions.trim(),
      });
      toast.success("Improvement started — it will appear in your history shortly");
      setImproveItem(null);
      setImproveInstructions("");
      fetchData();
    } catch {
      toast.error("Failed to start improvement");
    } finally {
      setIsImproving(false);
    }
  };

  const handleLoadVersions = async (item: AIContentItem) => {
    if (!token) return;
    setVersionsItem(item);
    setIsLoadingVersions(true);
    try {
      const result = await marketingApi.getAIContentVersions(token, item.id);
      setVersions(result);
    } catch {
      toast.error("Failed to load versions");
      setVersionsItem(null);
    } finally {
      setIsLoadingVersions(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Content History | AI Content | Portal</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">
              Content History
            </h1>
            <p className="mt-2 text-lg text-slate-600">
              View and manage previously generated content.
            </p>
          </div>
        </div>

        {/* Content panel */}
        <div className="space-y-6 rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          {/* Controls bar */}
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={() => setSearchTerm("")}
              placeholder="Search by title..."
              containerClassName="flex-1 min-w-[200px]"
            />
            <Select
              value={typeFilter}
              onValueChange={(v) =>
                setTypeFilter(v as AIContentType | "all")
              }
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="blog_post">Blog Post</SelectItem>
                <SelectItem value="case_study">Case Study</SelectItem>
                <SelectItem value="email_sequence">Email Sequence</SelectItem>
                <SelectItem value="social_post">Social Post</SelectItem>
                <SelectItem value="content_brief">Content Brief</SelectItem>
                <SelectItem value="topic_cluster">Topic Cluster</SelectItem>
                <SelectItem value="content_calendar">Content Calendar</SelectItem>
                <SelectItem value="competitive_analysis">Competitive Analysis</SelectItem>
                <SelectItem value="market_research">Market Research</SelectItem>
                <SelectItem value="audience_insight">Audience Insight</SelectItem>
                <SelectItem value="campaign_plan">Campaign Plan</SelectItem>
                <SelectItem value="social_calendar">Social Calendar</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={statusFilter}
              onValueChange={(v) =>
                setStatusFilter(v as AIContentStatus | "all")
              }
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="generating">Generating</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchData}>
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Refresh
            </Button>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : sortedData.length === 0 ? (
            <div className="py-12 text-center">
              <Clock className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-medium">
                {searchTerm ? "No items match your search" : "No content generated yet"}
              </h3>
              <p className="text-muted-foreground">
                {searchTerm
                  ? "Try a different search term."
                  : "Generate your first content to get started."}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    direction={getSortDirection("content_type")}
                    onSort={() => requestSort("content_type")}
                  >
                    Type
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("title")}
                    onSort={() => requestSort("title")}
                  >
                    Title
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("status")}
                    onSort={() => requestSort("status")}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("word_count")}
                    onSort={() => requestSort("word_count")}
                  >
                    Words
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("created_at")}
                    onSort={() => requestSort("created_at")}
                  >
                    Created
                  </SortableTableHead>
                  <SortableTableHead
                    direction={null}
                    onSort={() => {}}
                    disabled
                    className="w-[50px]"
                  >
                    {" "}
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((item) => {
                  const typeInfo = CONTENT_TYPE_LABELS[item.content_type];
                  const TypeIcon = typeInfo?.icon || FileText;
                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TypeIcon className="h-4 w-4 text-slate-400" />
                          <span className="text-sm">
                            {typeInfo?.label || item.content_type}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[300px] truncate font-medium">
                        {item.title}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            STATUS_VARIANTS[item.status] || "secondary"
                          }
                        >
                          {item.status === "completed" && (
                            <CheckCircle className="mr-1 h-3 w-3" />
                          )}
                          {item.status === "failed" && (
                            <XCircle className="mr-1 h-3 w-3" />
                          )}
                          {item.status === "generating" && (
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          )}
                          {item.status.charAt(0).toUpperCase() +
                            item.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {item.word_count ?? "-"}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {formatDate(item.created_at)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {item.status === "completed" && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => setViewItem(item)}
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  View
                                </DropdownMenuItem>
                                {item.rendered_content && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleCopy(item.rendered_content!)
                                    }
                                  >
                                    <Copy className="mr-2 h-4 w-4" />
                                    Copy
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => setImproveItem(item)}
                                >
                                  <Wand2 className="mr-2 h-4 w-4" />
                                  Improve
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleLoadVersions(item)}
                                >
                                  <GitBranch className="mr-2 h-4 w-4" />
                                  Versions
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                              </>
                            )}
                            <DropdownMenuItem
                              onClick={() => setDeleteId(item.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {totalItems > 0 && (
            <div className="mt-4 border-t pt-4">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                totalCount={totalItems}
                pageSize={itemsPerPage}
                pageSizeOptions={[25, 50, 100]}
                startIndex={startIndex}
                endIndex={endIndex}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                onPageChange={setCurrentPage}
                onPageSizeChange={setItemsPerPage}
                onFirstPage={() => setCurrentPage(1)}
                onLastPage={() => setCurrentPage(totalPages)}
                onPrevPage={() => setCurrentPage(currentPage - 1)}
                onNextPage={() => setCurrentPage(currentPage + 1)}
              />
            </div>
          )}
        </div>
      </div>

      {/* View Dialog */}
      <Dialog open={!!viewItem} onOpenChange={() => setViewItem(null)}>
        <DialogContent className="max-h-[80vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewItem?.title}</DialogTitle>
          </DialogHeader>
          {viewItem?.parent_content_id && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <GitBranch className="h-4 w-4" />
              <span>Improved from a previous version</span>
            </div>
          )}
          {viewItem?.rendered_content && (
            <div className="prose prose-sm max-w-none">
              <pre className="overflow-x-auto whitespace-pre-wrap break-words font-sans text-sm">
                {viewItem.rendered_content}
              </pre>
            </div>
          )}
          {viewItem?.research_data && Object.keys(viewItem.research_data).length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-blue-800">
                <Globe className="h-4 w-4" />
                Research Sources
              </div>
              <ul className="space-y-1 text-sm text-blue-700">
                {(viewItem.research_data.sources as string[] | undefined)?.map(
                  (source, i) => (
                    <li key={i} className="truncate">
                      <a
                        href={source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-blue-900"
                      >
                        {source}
                      </a>
                    </li>
                  ),
                ) ?? (
                  <li className="text-blue-600">
                    Research data available (see raw content)
                  </li>
                )}
              </ul>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4">
            {viewItem?.rendered_content && (
              <Button
                variant="outline"
                onClick={() => handleCopy(viewItem.rendered_content!)}
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete content?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the generated content from your history. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Improve Dialog */}
      <Dialog
        open={!!improveItem}
        onOpenChange={() => {
          setImproveItem(null);
          setImproveInstructions("");
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Improve Content</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Describe how you'd like to improve "{improveItem?.title}".
          </p>
          <Textarea
            placeholder="e.g. Make the tone more casual, add statistics, shorten the intro..."
            value={improveInstructions}
            onChange={(e) => setImproveInstructions(e.target.value)}
            rows={4}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setImproveItem(null);
                setImproveInstructions("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImprove}
              disabled={isImproving || !improveInstructions.trim()}
            >
              {isImproving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="mr-2 h-4 w-4" />
              )}
              Improve
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Versions Dialog */}
      <Dialog
        open={!!versionsItem}
        onOpenChange={() => {
          setVersionsItem(null);
          setVersions([]);
        }}
      >
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <GitBranch className="h-5 w-5" />
                Version History — {versionsItem?.title}
              </div>
            </DialogTitle>
          </DialogHeader>
          {isLoadingVersions ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : versions.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-500">
              No other versions found for this content.
            </p>
          ) : (
            <div className="space-y-3">
              {versions.map((ver, idx) => (
                <div
                  key={ver.id}
                  className="rounded-lg border p-3 hover:bg-slate-50 cursor-pointer"
                  onClick={() => {
                    setVersionsItem(null);
                    setVersions([]);
                    setViewItem(ver);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        v{idx + 1}
                      </Badge>
                      <span className="text-sm font-medium">{ver.title}</span>
                    </div>
                    <span className="text-xs text-slate-400">
                      {formatDate(ver.created_at)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
                    <Badge
                      variant={STATUS_VARIANTS[ver.status] || "secondary"}
                      className="text-xs"
                    >
                      {ver.status}
                    </Badge>
                    {ver.word_count && <span>{ver.word_count} words</span>}
                    {ver.parent_content_id && (
                      <span className="flex items-center gap-1">
                        <Wand2 className="h-3 w-3" /> Improved
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
